import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import i18n from '@/settings/i18n';
import { useProductPageContext } from '../../../hooks/useProductPageContext';
import { CHECKPOINTS_BY_CATEGORY } from './constants';

const t = i18n.init('product');

const Checkpoints = ({ className }: { className?: string }) => {
  const {
    selectedSkuDetails: { categoryName },
  } = useProductPageContext();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const CHECKPOINTS = CHECKPOINTS_BY_CATEGORY[categoryName];

  const updateScrollIndicators = () => {
    const el = scrollRef.current;

    if (!el) {
      return;
    }
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    updateScrollIndicators();
  }, [CHECKPOINTS]);

  if (!CHECKPOINTS) {
    return null;
  }

  return (
    <div className={classNames(`text-xs font-medium`, className)}>
      <span>{t`Professionally restored with 40+ points of inspection`}</span>
      <div className="relative mt-1">
        {canScrollLeft && (
          <div className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-14 bg-gradient-to-r from-white to-transparent" />
        )}
        {canScrollRight && (
          <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-14 bg-gradient-to-l from-white to-transparent" />
        )}
        <div
          ref={scrollRef}
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className="hide-scrollbar flex cursor-grab select-none items-center gap-x-2 overflow-auto active:cursor-grabbing"
          onScroll={updateScrollIndicators}
          onMouseDown={(event) => {
            if (!scrollRef.current) {
              return;
            }
            isDraggingRef.current = true;
            startXRef.current =
              event.pageX - scrollRef.current.getBoundingClientRect().left;
            scrollLeftRef.current = scrollRef.current.scrollLeft;
          }}
          onMouseLeave={() => {
            isDraggingRef.current = false;
          }}
          onMouseUp={() => {
            isDraggingRef.current = false;
          }}
          onMouseMove={(event) => {
            if (!scrollRef.current || !isDraggingRef.current) {
              return;
            }
            event.preventDefault();
            const x =
              event.pageX - scrollRef.current.getBoundingClientRect().left;
            const walk = x - startXRef.current;

            scrollRef.current.scrollLeft = scrollLeftRef.current - walk;
          }}
        >
          {CHECKPOINTS.map((checkpoint, index) => {
            const Icon = checkpoint.icon;

            return (
              <div
                key={index}
                className="flex h-8 shrink-0 items-center gap-2 rounded-full bg-[#F8F8F8] px-3.5"
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span>{t(checkpoint.title)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Checkpoints;
